import {
  getQrCampaign,
  resolveCampaignStoreUrl
} from './qr-campaigns.js';

export const DOWNLOAD_EVENTS = Object.freeze({
  pageViewed: 'App Download Page Viewed',
  storeSelected: 'App Download Store Selected'
});

export const AMPLITUDE_PRIVACY_OPTIONS = Object.freeze({
  autocapture: false,
  defaultTracking: false,
  identityStorage: 'none',
  fetchRemoteConfig: false,
  enableDiagnostics: false,
  diagnosticsSampleRate: 0,
  serverZone: 'US',
  trackingOptions: Object.freeze({
    ipAddress: false,
    language: false,
    platform: false
  })
});

const BLOCKED_AMPLITUDE_FIELDS = Object.freeze([
  'user_agent',
  'ip',
  'language',
  'platform'
]);

const VALID_PRODUCTS = new Set(['care', 'pro']);
const VALID_SOURCES = new Set(['direct', 'qr', 'campaign']);
const VALID_PLATFORMS = new Set(['ios', 'android', 'desktop', 'unknown']);
const VALID_LANGUAGES = new Set(['en', 'uk']);
const VALID_STORES = new Set(['app_store', 'google_play']);
const VALID_SELECTION_METHODS = new Set(['automatic', 'manual']);
const API_KEY = import.meta.env?.VITE_AMPLITUDE_LANDING_API_KEY?.trim() || '';
const APPLE_CAMPAIGN_URLS = Object.freeze({
  care_landing_download: import.meta.env?.VITE_APPLE_CAMPAIGN_CARE_LANDING_DOWNLOAD?.trim() || '',
  pro_landing_download: import.meta.env?.VITE_APPLE_CAMPAIGN_PRO_LANDING_DOWNLOAD?.trim() || '',
  care_practice_print: import.meta.env?.VITE_APPLE_CAMPAIGN_CARE_PRACTICE_PRINT?.trim() || ''
});

let amplitudeReady = false;
let amplitudeInitialisation;
let amplitudeClient;

export function getDownloadSource(params) {
  if (getQrCampaign(params.get('qr'))) return 'qr';
  const medium = params.get('utm_medium')?.toLowerCase();
  if (medium === 'qr') return 'qr';
  return params.has('utm_source') || params.has('utm_campaign') ? 'campaign' : 'direct';
}

export function detectDevicePlatform(userAgent = '', maxTouchPoints = 0) {
  if (/iPad|iPhone|iPod/i.test(userAgent)) return 'ios';
  if (/Macintosh/i.test(userAgent) && maxTouchPoints > 1) return 'ios';
  if (/Android/i.test(userAgent)) return 'android';
  if (/Windows|Macintosh|Linux/i.test(userAgent)) return 'desktop';
  return 'unknown';
}

export function resolveAutomaticStore(params, devicePlatform) {
  if (params.get('redirect') === '0') return null;

  const override = params.get('store');
  if (VALID_STORES.has(override)) return override;
  if (devicePlatform === 'ios') return 'app_store';
  if (devicePlatform === 'android') return 'google_play';
  return null;
}

export function buildDownloadEventProperties(input) {
  const properties = {
    product: VALID_PRODUCTS.has(input.product) ? input.product : 'care',
    source: VALID_SOURCES.has(input.source) ? input.source : 'direct',
    device_platform: VALID_PLATFORMS.has(input.devicePlatform) ? input.devicePlatform : 'unknown',
    language: VALID_LANGUAGES.has(input.language) ? input.language : 'en'
  };

  if (VALID_STORES.has(input.store)) properties.store = input.store;
  if (getQrCampaign(input.qrCampaign)) properties.qr_campaign = input.qrCampaign;
  if (VALID_SELECTION_METHODS.has(input.selectionMethod)) {
    properties.selection_method = input.selectionMethod;
  }
  return properties;
}

export function canInitialiseAmplitude(apiKey = API_KEY) {
  return typeof apiKey === 'string' && apiKey.trim().length > 0;
}

export function sanitizeAmplitudeEvent(event) {
  const sanitizedEvent = { ...event };
  BLOCKED_AMPLITUDE_FIELDS.forEach((field) => delete sanitizedEvent[field]);
  return sanitizedEvent;
}

export async function safelySettleAnalyticsDispatch(dispatch) {
  try {
    await dispatch;
    return true;
  } catch {
    return false;
  }
}

export const AMPLITUDE_PRIVACY_PLUGIN = {
  name: 'vetlyx-download-privacy-filter',
  type: 'enrichment',
  setup: async () => undefined,
  execute: async (event) => sanitizeAmplitudeEvent(event)
};

async function initialiseAmplitude() {
  if (!canInitialiseAmplitude()) return false;
  if (amplitudeReady) return true;
  if (amplitudeInitialisation) return amplitudeInitialisation;

  amplitudeInitialisation = import('@amplitude/analytics-browser')
    .then((amplitude) => {
      amplitudeClient = amplitude;
      return amplitude.init(API_KEY, undefined, AMPLITUDE_PRIVACY_OPTIONS).promise
        .then(() => amplitude.add(AMPLITUDE_PRIVACY_PLUGIN).promise);
    })
    .then(() => {
      amplitudeReady = true;
      return true;
    })
    .catch(() => false);

  return amplitudeInitialisation;
}

async function trackDownloadEvent(eventName, properties) {
  // The dataLayer event lets the existing consent-managed GTM container mirror
  // the same event without exposing URLs, query strings, or identifiers.
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'amplitude_event',
    amplitude_event_name: eventName,
    amplitude_event_properties: properties
  });

  if (await initialiseAmplitude()) {
    await safelySettleAnalyticsDispatch(amplitudeClient.track(eventName, properties).promise);
  }
}

export function completeAutomaticStoreRedirect(
  page,
  href,
  navigate = (target) => window.location.replace(target)
) {
  delete page.dataset.downloadRedirecting;
  const status = page.querySelector('[data-download-status]');
  if (status) status.hidden = true;
  navigate(href);
}

function redirectToStore(page, store, context) {
  const link = page.querySelector(`[data-download-store="${store}"]`);
  if (!link?.href) return;

  page.dataset.downloadRedirecting = 'true';
  const status = page.querySelector('[data-download-status]');
  if (status) status.hidden = false;

  const tracking = trackDownloadEvent(
    DOWNLOAD_EVENTS.storeSelected,
    buildDownloadEventProperties({ ...context, store, selectionMethod: 'automatic' })
  );

  // Never hold up the install journey when analytics is unavailable or blocked.
  Promise.race([tracking, new Promise((resolve) => setTimeout(resolve, 300))])
    .finally(() => completeAutomaticStoreRedirect(page, link.href));
}

export function initDownloadAnalytics() {
  const page = document.querySelector('[data-download-page]');
  if (!page) return;

  const params = new URLSearchParams(window.location.search);
  const context = {
    product: page.dataset.downloadProduct,
    source: getDownloadSource(params),
    qrCampaign: getQrCampaign(params.get('qr'))?.id,
    devicePlatform: detectDevicePlatform(
      window.navigator.userAgent,
      window.navigator.maxTouchPoints
    ),
    language: document.documentElement.lang === 'uk' ? 'uk' : 'en'
  };

  void trackDownloadEvent(DOWNLOAD_EVENTS.pageViewed, buildDownloadEventProperties(context));

  const qrCampaign = getQrCampaign(context.qrCampaign);
  page.querySelectorAll('[data-download-store]').forEach((link) => {
    link.href = resolveCampaignStoreUrl({
      campaign: qrCampaign,
      store: link.dataset.downloadStore,
      fallbackUrl: link.href,
      appleCampaignUrls: APPLE_CAMPAIGN_URLS
    });

    link.addEventListener('click', () => {
      void trackDownloadEvent(
        DOWNLOAD_EVENTS.storeSelected,
        buildDownloadEventProperties({
          ...context,
          store: link.dataset.downloadStore,
          selectionMethod: 'manual'
        })
      );
    });
  });

  const automaticStore = resolveAutomaticStore(params, context.devicePlatform);
  if (automaticStore) redirectToStore(page, automaticStore, context);
}
